const SIMPLE_NEWSLETTER_STORAGE_KEY = 'newsletter_seen';
const SIMPLE_NEWSLETTER_OPEN_DELAY = 3000;
const THEME_POPUP_ID = 'newsletter-popup';
const THEME_PAGE_COUNTER_COOKIE_DAYS = 365;
const MOBILE_BREAKPOINT = 750;
const THEME_SCROLL_POSITION_KEY = 'newsletter-popup:scroll-position';
const THEME_SUBMIT_PENDING_KEY = 'newsletter-popup:submit-pending';

function toNumber(value, fallback = 0) {
  if (value === null || value === undefined) return fallback;

  const normalizedValue = String(value).trim();
  if (normalizedValue === '') return fallback;

  const parsed = Number(normalizedValue);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function isMobileDevice() {
  return window.innerWidth < MOBILE_BREAKPOINT;
}

function getLocalStorageItem(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function setLocalStorageItem(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {}
}

function getSessionStorageItem(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function setSessionStorageItem(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {}
}

function removeSessionStorageItem(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {}
}

function getCookie(name) {
  const encodedName = encodeURIComponent(name);
  const parts = document.cookie ? document.cookie.split('; ') : [];

  for (const part of parts) {
    const separatorIndex = part.indexOf('=');
    const key = separatorIndex === -1 ? part : part.slice(0, separatorIndex);

    if (key !== encodedName) continue;

    const value = separatorIndex === -1 ? '' : part.slice(separatorIndex + 1);
    return decodeURIComponent(value);
  }

  return null;
}

function setCookie(name, value, days, path = '/') {
  const encodedName = encodeURIComponent(name);
  const encodedValue = encodeURIComponent(value);
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${encodedName}=${encodedValue}; expires=${expires}; path=${path}`;
}

function sanitizeThemeName(value) {
  return String(value || 'theme')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'theme';
}

function openPopupElement(popup, dialog) {
  if (dialog?.open) return;

  if (typeof popup.showDialog === 'function') {
    popup.showDialog();
    return;
  }

  if (typeof dialog?.showModal === 'function') {
    dialog.showModal();
    return;
  }

  popup.hidden = false;
  popup.classList.add('is-visible');
}

function closePopupElement(popup, dialog) {
  if (typeof popup.closeDialog === 'function') {
    popup.closeDialog();
    return;
  }

  if (dialog?.open && typeof dialog.close === 'function') {
    dialog.close();
    return;
  }

  popup.hidden = true;
  popup.classList.remove('is-visible');
}

function restoreThemeScrollPosition() {
  const savedScrollY = toNumber(getSessionStorageItem(THEME_SCROLL_POSITION_KEY), -1);
  if (savedScrollY < 0) return;

  window.scrollTo({ top: savedScrollY, behavior: 'instant' });
}

function clearThemeSubmitState() {
  removeSessionStorageItem(THEME_SCROLL_POSITION_KEY);
  removeSessionStorageItem(THEME_SUBMIT_PENDING_KEY);
}

function initSimpleCustomNewsletterPopup() {
  const simplePopups = document.querySelectorAll('[data-newsletter-simple-popup]');
  if (!simplePopups.length) return;

  if (getLocalStorageItem(SIMPLE_NEWSLETTER_STORAGE_KEY)) return;

  for (const popup of simplePopups) {
    const dialog = popup.matches('dialog') ? popup : popup.querySelector('dialog');
    const closeButtons = popup.querySelectorAll('[data-newsletter-simple-close], .newsletter-popup__close, .close-button');

    const hidePopup = () => {
      closePopupElement(popup, dialog);
      setLocalStorageItem(SIMPLE_NEWSLETTER_STORAGE_KEY, 'true');
    };

    window.setTimeout(() => {
      openPopupElement(popup, dialog);
    }, SIMPLE_NEWSLETTER_OPEN_DELAY);

    for (const button of closeButtons) {
      button.addEventListener('click', hidePopup);
    }

    if (dialog) {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) {
          hidePopup();
        }
      });
    }

    popup.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target === popup || target.hasAttribute('data-newsletter-simple-overlay')) {
        hidePopup();
      }
    });
  }
}

function initThemeNewsletterPopup() {
  const popup = document.getElementById(THEME_POPUP_ID);
  if (!popup) return;

  const dialog = popup.querySelector('dialog');
  if (!dialog) return;

  const form = popup.querySelector('#NewsletterPopupForm');
  const popupVersion = popup.dataset.version || '1';
  const themeName = sanitizeThemeName(popup.dataset.themeName);
  const newsletterCookieKey = `${themeName}_newsletter${popupVersion}`;
  const shownPagesCookieKey = `${themeName}_shown_pages`;
  const dayNext = toNumber(popup.dataset.dayNext, 7);
  const subscribeDayNext = toNumber(popup.dataset.subscribeDayNext, 30);
  const numberPages = toNumber(popup.dataset.pageCountTarget, 0);
  const scrollDelay = toNumber(popup.dataset.scrollDelay, 800);
  const timeDelay = toNumber(popup.dataset.timeDelay, 2) * 1000;
  const checkbox = popup.querySelector('[data-newsletter-popup-hide]');
  const mobileEnabled = popup.dataset.mobileEnabled !== 'false';
  const desktopEnabled = popup.dataset.desktopEnabled !== 'false';
  const isMobile = isMobileDevice();
  const successMessageElement = popup.querySelector('.newsletter-popup__message--success');
  const errorMessageElement = popup.querySelector('.newsletter-popup__message--error');
  const input = popup.querySelector('#NewsletterPopupEmail');
  const hasSuccess = Boolean(successMessageElement);
  const hasError = Boolean(errorMessageElement);
  const popupSubmitPending = getSessionStorageItem(THEME_SUBMIT_PENDING_KEY) === 'true';
  const isCustomerFormResponse =
    new URL(window.location.href).searchParams.get('customer_posted') === 'true' || window.location.hash === '#contact_form';
  const hasPopupSuccess = popupSubmitPending && hasSuccess;
  const hasPopupError = popupSubmitPending && hasError;

  let triggerMode = popup.dataset.after || 'scroll';
  let openDelay = timeDelay;
  let wasSubscribed = hasPopupSuccess;

  const saveNewsletterShown = (days = dayNext) => {
    setCookie(newsletterCookieKey, 'shown', days, '/');
  };

  const openThemePopup = ({ force = false } = {}) => {
    if (!force && getCookie(newsletterCookieKey) === 'shown') return;
    openPopupElement(popup, dialog);
  };

  const scheduleAutoOpen = (delay, options = {}) => {
    window.setTimeout(() => {
      openThemePopup(options);
    }, delay);
  };

  const currentShownPages = () => {
    return toNumber(getCookie(shownPagesCookieKey), 0);
  };

  const incrementShownPages = () => {
    const nextValue = currentShownPages() + 1;
    setCookie(shownPagesCookieKey, String(nextValue), THEME_PAGE_COUNTER_COOKIE_DAYS, '/');
  };

  const isPopupEnabledForCurrentDevice = () => {
    return isMobile ? mobileEnabled : desktopEnabled;
  };

  if (popupSubmitPending && !isCustomerFormResponse) {
    clearThemeSubmitState();
  }

  if (!popupSubmitPending && (hasSuccess || hasError)) {
    successMessageElement?.remove();
    errorMessageElement?.remove();
    input?.removeAttribute('aria-describedby');
    input?.removeAttribute('aria-invalid');
  }

  if (form) {
    form.addEventListener('submit', () => {
      setSessionStorageItem(THEME_SCROLL_POSITION_KEY, String(window.scrollY));
      setSessionStorageItem(THEME_SUBMIT_PENDING_KEY, 'true');
    });
  }

  dialog.addEventListener('close', () => {
    saveNewsletterShown(wasSubscribed ? subscribeDayNext : dayNext);
  });

  if (hasPopupSuccess) {
    saveNewsletterShown(subscribeDayNext);
    restoreThemeScrollPosition();
    triggerMode = 'auto';
    openDelay = 500;
    scheduleAutoOpen(openDelay, { force: true });
    clearThemeSubmitState();
    return;
  }

  if (hasPopupError) {
    restoreThemeScrollPosition();
    triggerMode = 'auto';
    openDelay = 500;
    scheduleAutoOpen(openDelay, { force: true });
    clearThemeSubmitState();
    return;
  }

  const observer = new MutationObserver(() => {
    const successMessage = popup.querySelector('.newsletter-popup__message--success');
    if (!successMessage) return;

    wasSubscribed = true;
    saveNewsletterShown(subscribeDayNext);
    openThemePopup({ force: true });
    observer.disconnect();
  });

  observer.observe(popup, {
    childList: true,
    subtree: true,
  });

  if (getCookie(newsletterCookieKey) === 'shown') return;
  if (!isPopupEnabledForCurrentDevice()) return;

  if (numberPages > currentShownPages()) {
    incrementShownPages();
    return;
  }

  if (triggerMode === 'scroll') {
    const handleScroll = () => {
      if (window.scrollY < scrollDelay) return;
      window.removeEventListener('scroll', handleScroll);
      openThemePopup();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return;
  }

  scheduleAutoOpen(openDelay);
}

function initializeNewsletterPopups() {
  initSimpleCustomNewsletterPopup();

  const startThemePopup = () => {
    initThemeNewsletterPopup();
  };

  if (customElements.get('dialog-component')) {
    startThemePopup();
    return;
  }

  customElements.whenDefined('dialog-component').then(startThemePopup);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeNewsletterPopups);
} else {
  initializeNewsletterPopups();
}
